import { isInstanceOf } from '@packages/toolbox';

/* eslint-disable @typescript-eslint/no-unused-expressions */
import { HTTP_CODE_ERROR } from '@packages/toolbox/dist/constants/http-codes';
import { set } from 'lodash';
import { useLoading } from 'SharedDir/common/composables/useLoading';
import { AxiosError, AxiosResponse } from 'axios';
import isObject from 'lodash/isObject';
import { NotificationTypes, useNotifications } from 'SharedDir/common/composables/useNotifications';
import { FIELDS_DICTIONARY } from 'SharedDir/constants/field-translate';
import { NPydantic } from 'SharedDir/declarations/pydantic';
import {
  ErrorBag,
  PydanticValidationError,
} from 'SharedDir/utils/errors/pydantic-validation-error';

export interface MakeRequestOptions {
  useLoader: boolean;
  catchErrors: boolean;
  skipHttpCodes: Array<number>;
  dontAlert: boolean | Array<unknown>;
  defaultMessage: string;
  startLoading?: () => Promise<void> | void;
  finishLoading?: () => Promise<void> | void;
}

export interface SilentRequestOptions extends MakeRequestOptions {
  silent: true;
}

export const ifAxiosError = (error: unknown): boolean => {
  if (isObject(error) && typeof (error as any)?.request !== 'undefined') {
    return true;
  }

  return false;
};

export const ifValidationError = (error: unknown): boolean => {
  if (!ifAxiosError(error)) {
    return false;
  }
  return (error as AxiosError)?.response?.status === HTTP_CODE_ERROR.LOGIC_ERROR;
};

const shouldWeAlert = (error: unknown, options: MakeRequestOptions): boolean => {
  if (options.dontAlert === true) {
    return false;
  }

  return !(Array.isArray(options.dontAlert) && isInstanceOf(error, options.dontAlert));
};

export const shouldWeCatch = (error: unknown, options: MakeRequestOptions): boolean => {
  if (!options.catchErrors) {
    return false;
  }

  if (
    ifAxiosError(error) &&
    (error as AxiosError)?.response?.status &&
    options.skipHttpCodes.includes((error as AxiosError)?.response?.status || 0)
  ) {
    return false;
  }

  return true;
};

export const getErrorMessage = (error: unknown): undefined | string => {
  if (ifAxiosError(error)) {
    return (
      ((error as AxiosError).response as AxiosResponse<{ detail: string }>)?.data?.detail ||
      ((error as AxiosError).response as AxiosResponse<{ message: string }>)?.data?.message
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return undefined;
};

export function makeErrorBag(error: AxiosError<NPydantic.ValidationDetail>): ErrorBag {
  const { detail } = error.response?.data || { detail: [] };
  const validationErrors = {};
  detail.forEach(({ loc, ...rest }) => {
    set(validationErrors, loc, rest);
  });
  return validationErrors;
}

export function translateField(fieldName: string): string {
  const translation = FIELDS_DICTIONARY[fieldName];
  if (typeof translation === 'undefined') {
    console.warn(
      `Couldn't find translation entry for field "${fieldName}". Did you forget add it to FIELDS_DICTIONARY object?`
    );
  }
  return translation ?? fieldName;
}

export function translateIfCan(message: string): string {
  let translatedMessage = message;

  Object.entries(translatedMessage).forEach(([key, value]) => {
    const messageTranslatePosition = message.indexOf(key);
    if (messageTranslatePosition >= 0) {
      translatedMessage = translatedMessage.replace(key, value);
    }
  });

  return translatedMessage;
}

/**
 * Проверить является ли ошибка ответом с типом blob
 * и имеется ли в нем json
 *
 * @param error
 * @returns
 */
export function isBlobError(error: any): boolean {
  const isBlob = error?.request?.responseType === 'blob' &&
    error?.response?.data instanceof Blob &&
    error?.response?.data?.type &&
    error?.response?.data?.type.toLowerCase().indexOf('json') !== -1;

  return isBlob;
}

/**
 * Парсинг ошибки с ответа, который имеет тип blob
 *
 * @param error
 * @returns
 */
export async function getBlobErrorMessage(error: any): Promise<string> {
  const errorObj = JSON.parse(await error.response.data.text());
  if (errorObj.message || errorObj.detail) {
    const message = errorObj.message || errorObj.detail;
    if (message) {
      return message;
    }
  }

  return 'При выполнении запроса произошла ошибка';
}

export function notifyValidationErrors(bag: ErrorBag): void {
  const { failureNotification } = useNotifications();
  console.debug(bag);
  Object.keys(bag).forEach((key) => {
    Object.keys(bag[key]).forEach((fieldName) => {
      const { msg } = bag[key][fieldName];
      let translatedMessage = msg;
      try {
        translatedMessage = translateIfCan(msg);
      } catch (err) {
        console.log(msg, bag, key, fieldName);
        console.log(err);
      }
      failureNotification(
        `Ошибка валидации: "${translateField(fieldName)}" - ${translatedMessage || msg}`
      );
    });
  });
}

const defaultMakeRequestOptions: MakeRequestOptions = {
  useLoader: true,
  catchErrors: true,
  skipHttpCodes: [401, 429],
  dontAlert: false,
  defaultMessage: 'При выполнении запроса произошла ошибка',
  startLoading: () => {
    const { general } = useLoading();
    // console.debug('Standard "startLoading" was used!');
    general.show();
  },
  finishLoading: () => {
    const { general } = useLoading();
    // console.debug('Standard "finishLoading" was used!');
    general.hide();
  },
};

export const makeRequest = async <
  T,
  O extends Partial<MakeRequestOptions> | Partial<SilentRequestOptions>
>(
  request: () => Promise<T>,
  options?: O
): Promise<O extends SilentRequestOptions ? T | undefined : T> => {
  const { failureNotification } = useNotifications();
  const o = { ...defaultMakeRequestOptions, ...((options as Partial<SilentRequestOptions>) || {}) };
  try {
    o.useLoader && o.startLoading && (await o.startLoading());
    return await request();
  } catch (e) {
    if (shouldWeCatch(failureNotification, o) && shouldWeAlert(e, o)) {
      if (ifValidationError(e)) {
        const errorBag = makeErrorBag(e as any);
        // eslint-disable-next-line no-ex-assign
        e = new PydanticValidationError(errorBag);
        notifyValidationErrors(errorBag);
      } else {
        let message = '';
        // В случае проблем с сетью
        if (e && (e as any).code === 'ERR_NETWORK') {
          message = 'Ошибка соединения с сервером, через несколько минут всё заработает!';
        } else {
          message = getErrorMessage(e) ?? o.defaultMessage;
        }

        failureNotification(message);
      }
    }
    if (o.silent) {
      return undefined as O extends SilentRequestOptions ? T | undefined : T;
    }
    throw e;
  } finally {
    o.useLoader && o.finishLoading && (await o.finishLoading());
  }
};
